import { useState, useRef, useEffect, useCallback } from "react";
import { api, getErrorMessage } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Sparkles, Mic, Square, Send, Loader2, Check, X, AlertTriangle, ArrowUpRight, Lightbulb, Zap } from "lucide-react";
import { toast } from "sonner";
import { Link } from "react-router-dom";

function confidenceColor(c) {
  if (c >= 0.75) return "bg-emerald-100 text-emerald-800 border-emerald-300";
  if (c >= 0.45) return "bg-amber-100 text-amber-800 border-amber-300";
  return "bg-red-100 text-red-800 border-red-300";
}

/**
 * Global floating AI button. Opens a compact panel in the bottom-right of the
 * app, powered by the same /api/ai/* endpoints as the full page.
 */
export const FloatingAIAssistant = () => {
  const [open, setOpen] = useState(false);
  const [text, setText] = useState("");
  const [action, setAction] = useState(null);
  const [recording, setRecording] = useState(false);
  const [transcribing, setTranscribing] = useState(false);
  const [interpreting, setInterpreting] = useState(false);
  const [executing, setExecuting] = useState(false);
  const [suggestions, setSuggestions] = useState([]);
  const [macros, setMacros] = useState([]);
  const mediaRecorderRef = useRef(null);
  const chunksRef = useRef([]);
  const textareaRef = useRef(null);

  const loadHelpers = useCallback(async () => {
    try {
      const [sugg, macs] = await Promise.all([api.aiSuggestions(3), api.aiListMacros()]);
      setSuggestions(sugg.data?.suggestions || []);
      setMacros(macs.data || []);
    } catch { /* silent */ }
  }, []);

  useEffect(() => {
    if (open) {
      if (textareaRef.current) textareaRef.current.focus();
      loadHelpers();
    }
  }, [open, loadHelpers]);

  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mr = new MediaRecorder(stream, { mimeType: "audio/webm" });
      chunksRef.current = [];
      mr.ondataavailable = (e) => { if (e.data.size > 0) chunksRef.current.push(e.data); };
      mr.onstop = async () => {
        stream.getTracks().forEach((t) => t.stop());
        const blob = new Blob(chunksRef.current, { type: "audio/webm" });
        setTranscribing(true);
        try {
          const r = await api.aiTranscribe(blob);
          setText((prev) => (prev ? `${prev} ${r.data?.text || ""}` : r.data?.text || ""));
        } catch (e) {
          toast.error(getErrorMessage(e, "Transcription échouée"));
        } finally {
          setTranscribing(false);
        }
      };
      mr.start();
      mediaRecorderRef.current = mr;
      setRecording(true);
    } catch (e) {
      toast.error("Micro inaccessible : " + e.message);
    }
  };

  const stopRecording = () => {
    if (mediaRecorderRef.current && mediaRecorderRef.current.state !== "inactive") {
      mediaRecorderRef.current.stop();
    }
    setRecording(false);
  };

  const interpret = async (override) => {
    const raw = typeof override === "string" ? override : text;
    if (!raw?.trim()) return;
    setInterpreting(true); setAction(null);
    try {
      const r = await api.aiInterpret(raw.trim());
      setAction(r.data);
    } catch (e) {
      toast.error(getErrorMessage(e, "Erreur"));
    } finally {
      setInterpreting(false);
    }
  };

  const executeAction = async () => {
    if (!action?.action_id) return;
    setExecuting(true);
    try {
      await api.aiExecute(action.action_id);
      toast.success("Action exécutée");
      setAction(null); setText("");
      loadHelpers();
    } catch (e) {
      toast.error(getErrorMessage(e, "Échec"));
    } finally {
      setExecuting(false);
    }
  };

  const cancelAction = async () => {
    if (action?.action_id) {
      try { await api.aiCancel(action.action_id); } catch { /* ignore */ }
    }
    setAction(null);
  };

  const notActionable = action && (action.action_type === "none" || (action.confidence || 0) < 0.3);

  return (
    <>
      {!open && (
        <button
          type="button"
          onClick={() => setOpen(true)}
          className="fixed bottom-20 right-5 z-50 h-14 w-14 rounded-full bg-primary text-white shadow-xl hover:scale-105 active:scale-95 transition-transform grid place-items-center"
          title="Assistant IA"
          data-testid="floating-ai-button"
        >
          <Sparkles className="h-6 w-6" />
        </button>
      )}

      {open && (
        <div
          className="fixed bottom-20 right-5 z-50 w-[min(420px,92vw)] max-h-[min(600px,85vh)] rounded-xl border bg-card shadow-2xl flex flex-col overflow-hidden"
          data-testid="floating-ai-panel"
        >
          <div className="flex items-center justify-between px-4 py-3 border-b bg-gradient-to-r from-primary/10 to-primary/5">
            <div className="flex items-center gap-2 font-heading font-bold">
              <Sparkles className="h-4 w-4 text-primary" /> Assistant IA
            </div>
            <div className="flex items-center gap-1">
              <Button asChild size="icon" variant="ghost" title="Ouvrir la page complète">
                <Link to="/ia"><ArrowUpRight className="h-4 w-4" /></Link>
              </Button>
              <Button size="icon" variant="ghost" onClick={() => { setOpen(false); cancelAction(); }} data-testid="floating-ai-close">
                <X className="h-4 w-4" />
              </Button>
            </div>
          </div>

          <div className="flex-1 overflow-y-auto p-3 space-y-3">
            {macros.length > 0 && (
              <div className="flex items-center gap-1.5 flex-wrap" data-testid="floating-ai-macros">
                <Zap className="h-3 w-3 text-primary shrink-0" />
                {macros.slice(0, 4).map((m) => (
                  <button
                    key={m.id}
                    type="button"
                    onClick={() => { setText(m.prompt); interpret(m.prompt); }}
                    disabled={interpreting}
                    className="text-xs px-2 py-0.5 rounded-full border border-primary/20 bg-primary/5 hover:bg-primary/15 transition-colors disabled:opacity-50"
                    title={m.prompt}
                    data-testid={`floating-ai-macro-${m.id}`}
                  >
                    {m.emoji || "⚡"} {m.title}
                  </button>
                ))}
              </div>
            )}

            {suggestions.length > 0 && !action && (
              <div className="flex items-start gap-1 flex-wrap" data-testid="floating-ai-suggestions">
                <Lightbulb className="h-3 w-3 text-muted-foreground mt-1 shrink-0" />
                {suggestions.map((s, i) => {
                  const label = s.display_text || s.text;
                  return (
                  <button
                    key={i}
                    type="button"
                    onClick={() => setText(s.text)}
                    className="text-[11px] px-1.5 py-0.5 rounded border bg-muted/40 hover:bg-muted transition-colors text-left truncate max-w-[180px]"
                    title={label}
                    data-testid={`floating-ai-suggestion-${i}`}
                  >
                    {label.length > 32 ? `${label.slice(0, 30)}…` : label}
                  </button>
                );})}
              </div>
            )}

            <Textarea
              ref={textareaRef}
              value={text}
              onChange={(e) => setText(e.target.value)}
              placeholder="Ex : Jean a travaillé sur le chantier Martin hier"
              rows={3}
              disabled={interpreting || transcribing}
              data-testid="floating-ai-input"
            />

            <div className="flex items-center gap-2 flex-wrap">
              {!recording ? (
                <Button size="sm" variant="outline" onClick={startRecording} disabled={interpreting || transcribing} data-testid="floating-ai-mic-start">
                  {transcribing ? <><Loader2 className="h-4 w-4 mr-1 animate-spin" /> Trans…</> : <><Mic className="h-4 w-4 mr-1" /> Dicter</>}
                </Button>
              ) : (
                <Button size="sm" variant="destructive" onClick={stopRecording}>
                  <Square className="h-4 w-4 mr-1" /> Stop
                </Button>
              )}
              <Button size="sm" onClick={() => interpret()} disabled={interpreting || !text.trim()} data-testid="floating-ai-interpret">
                {interpreting ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Send className="h-4 w-4 mr-1" />}
                Interpréter
              </Button>
              {recording && (
                <span className="text-xs text-red-600 flex items-center gap-1">
                  <span className="h-2 w-2 rounded-full bg-red-600 animate-pulse" /> Enr…
                </span>
              )}
            </div>

            {action && (
              <div className="border-2 border-primary/30 rounded-lg p-3 space-y-2 bg-primary/5" data-testid="floating-ai-preview">
                <div className="flex items-center justify-between gap-2">
                  <div className="font-semibold text-sm">{action.summary}</div>
                  <Badge className={`${confidenceColor(action.confidence)} font-mono text-xs shrink-0`}>
                    {Math.round((action.confidence || 0) * 100)}%
                  </Badge>
                </div>
                {action.ambiguities?.length > 0 && (
                  <div className="text-xs text-amber-800 bg-amber-50 rounded p-2 flex items-start gap-1">
                    <AlertTriangle className="h-3 w-3 mt-0.5 shrink-0" />
                    <span>{action.ambiguities[0]}</span>
                  </div>
                )}
                <div className="flex items-center justify-end gap-2 pt-1">
                  <Button size="sm" variant="outline" onClick={cancelAction} disabled={executing}>
                    <X className="h-3 w-3 mr-1" /> Annuler
                  </Button>
                  <Button size="sm" onClick={executeAction} disabled={executing || notActionable} data-testid="floating-ai-confirm">
                    {executing ? <Loader2 className="h-3 w-3 mr-1 animate-spin" /> : <Check className="h-3 w-3 mr-1" />}
                    Valider
                  </Button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </>
  );
};

export default FloatingAIAssistant;
